import React, { createContext, useContext, ReactNode } from 'react'
import {
  useNotifications,
  type NotifResource,
  type Subscriber,
  type ConnectionState,
} from '../hooks/useNotifications'

interface NotificationsContextValue {
  connectionState: ConnectionState
  isConnected: boolean
  subscribe: (resource: NotifResource, callback: Subscriber) => () => void
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null)

interface ProviderProps {
  children: ReactNode
  /** Se false (es. utente non autenticato), il provider non apre la connessione. */
  enabled?: boolean
}

/**
 * Provider singleton per il canale realtime.
 *
 * Una sola connessione WebSocket condivisa per tutta l'app — più tab nello
 * stesso utente apriranno N connessioni indipendenti (ogni tab ha il suo
 * WebSocket), ma per la stessa SPA c'è una sola.
 *
 * Le pagine consumano via `useNotificationsContext().subscribe(resource, cb)`.
 */
export function NotificationsProvider({ children, enabled = true }: ProviderProps) {
  const { connectionState, isConnected, subscribe } = useNotifications({
    autoConnect: enabled,
  })

  return (
    <NotificationsContext.Provider value={{ connectionState, isConnected, subscribe }}>
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotificationsContext(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext)
  if (!ctx) {
    throw new Error('useNotificationsContext must be used within NotificationsProvider')
  }
  return ctx
}

/**
 * Hook ergonomico per sottoscriversi a una risorsa con auto-cleanup.
 * Da usare DENTRO un useEffect non serve: il cleanup è già gestito.
 *
 * Esempio:
 *   useResourceSubscription('task', () => fetchAllTasks())
 */
export function useResourceSubscription(
  resource: NotifResource,
  callback: Subscriber,
  deps: React.DependencyList = [],
) {
  const { subscribe } = useNotificationsContext()
  React.useEffect(() => {
    const unsubscribe = subscribe(resource, callback)
    return unsubscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, subscribe, ...deps])
}

/** Versione opzionale per componenti riusabili anche fuori dalla shell autenticata. */
export function useOptionalResourceSubscription(
  resource: NotifResource,
  callback: Subscriber,
  deps: React.DependencyList = [],
) {
  const ctx = useContext(NotificationsContext)
  React.useEffect(() => {
    if (!ctx) return undefined
    return ctx.subscribe(resource, callback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, ctx, ...deps])
}
